import type {
  AutomationFailureResponse,
  GetAutomationFailuresQueryKey,
} from '@urbanx/agx.support.client';
import { DATE_ONLY_FORMAT } from '@urbanx/agx.ui-components2';
import dayjs from 'dayjs';
import type { AutomationErrorFilterValues } from './AutomationErrorFilters';
import {
  toAutomationFailureBrowserDate,
  toAutomationFailureNextStartOfBrowserDayInstant,
  toAutomationFailureStartOfBrowserDayInstant,
} from './automationFailureDateTime';

export interface AutomationFailureListQuery {
  search?: string;
  area?: AutomationErrorFilterValues['area'];
  reason?: AutomationErrorFilterValues['reason'];
  agencyId?: string;
  campaignId?: string;
  status?: AutomationErrorFilterValues['status'];
  occurredFromUtc?: string;
  occurredToUtc?: string;
}

const getOccurredBounds = (
  occurredPeriod: AutomationErrorFilterValues['occurredPeriod'],
  nowInstant: string
) => {
  if (occurredPeriod === 'allTime') return {};

  const today = toAutomationFailureBrowserDate(nowInstant);
  const days = occurredPeriod === 'today' ? 1 : occurredPeriod === 'last7Days' ? 7 : 30;
  const firstDay = dayjs(today, DATE_ONLY_FORMAT, true).subtract(days - 1, 'day');

  if (!firstDay.isValid()) {
    throw new Error('Unable to calculate the automation error date range.');
  }

  return {
    occurredFromUtc: toAutomationFailureStartOfBrowserDayInstant(firstDay.format(DATE_ONLY_FORMAT)),
    occurredToUtc: toAutomationFailureNextStartOfBrowserDayInstant(today),
  };
};

export const buildAutomationFailureListQuery = (
  filters: AutomationErrorFilterValues,
  campaignId?: string,
  nowInstant: string = new Date().toISOString()
): AutomationFailureListQuery => {
  if (campaignId) return { campaignId };

  return {
    search: filters.search?.trim() || undefined,
    area: filters.area,
    reason: filters.reason,
    agencyId: filters.agencyId,
    status: filters.status,
    ...getOccurredBounds(filters.occurredPeriod, nowInstant),
  };
};

export const getAutomationFailuresQueryKey = (
  query: AutomationFailureListQuery,
  continuationToken?: string,
  pageSize?: number
): GetAutomationFailuresQueryKey => [
  'getAutomationFailures',
  query.search,
  query.area,
  query.reason,
  query.agencyId,
  query.campaignId,
  query.status,
  query.occurredFromUtc,
  query.occurredToUtc,
  continuationToken,
  pageSize,
];

export const mergeAutomationFailurePages = (
  current: AutomationFailureResponse[],
  nextPage: AutomationFailureResponse[]
) => {
  const references = new Map(
    current.flatMap((failure, index) =>
      failure.reference ? [[failure.reference, index] as const] : []
    )
  );
  const merged = [...current];

  nextPage.forEach((failure) => {
    const existingIndex = failure.reference ? references.get(failure.reference) : undefined;
    if (existingIndex === undefined) {
      if (failure.reference) references.set(failure.reference, merged.length);
      merged.push(failure);
      return;
    }

    merged[existingIndex] = failure;
  });

  return merged;
};
